/**
 * PlayjectorService - Production implementation using Electron IPC
 *
 * Wraps the actual Playjector IPC communication for production use.
 * Only available when running in Electron (not in browser).
 *
 * Scope: PlayjectorCoordinator only
 */

import type {
  IPlayjectorService,
  PlayjectorState,
  CampaignLaunchResult,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
  MacroStepReply,
  ScheduledScreenshotSpec,
  ScheduledScreenshotResult,
} from './interfaces/IPlayjectorService';
import { ipcSend } from '../../api/messaging/protocols/ipc';
import {
  generators,
  PlaycastMessage,
  type PlaycastMessageProcessMacroStep,
} from '@playcastdotio/Messaging';
import { playjectorHostModeState } from '../../api/stores/shared';
import { PlaycastHostEdition } from '@playcastdotio/CoreTypes';
import * as Sentry from '@sentry/react';

/**
 * How long to wait for a `processMacroStepReplyMSG` from Playjector before
 * rejecting the in-flight `requestMacroStep` promise. Sized generously — the
 * C++ side serializes the screenshot capture, hash compute, and JSON reply,
 * but typical replies arrive in well under 2s.
 */
const MACRO_STEP_REPLY_TIMEOUT_MS = 10_000;

/**
 * Variant key used when requestScheduledScreenshot wraps a macro step request.
 * The watcher only needs a single capture, so we always use this key.
 */
const SCHEDULED_VARIANT_KEY = 'scheduled';

interface MacroStepWaiter {
  resolve(value: Record<string, MacroStepCaptureResult>): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export class PlayjectorService implements IPlayjectorService {
  private campaignLaunchListeners: Array<(result: CampaignLaunchResult) => void> = [];
  private pendingMacroStep: MacroStepWaiter | null = null;

  constructor() {
    console.log('[PlayjectorService] 🚀 Playjector service initialized (Electron mode)');
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && window.playcast?.isHost === true;
  }

  sendHostModeChanged(
    edition: PlaycastHostEdition,
    matchmakeable: boolean,
    steamGuestLogin: boolean,
    guestAccount: boolean,
    credentials: string,
    hostAuthId: string,
    campaignId?: string,
  ): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Sending hostModeChanged via IPC');
    // Note: campaignId is not used by real IPC (Playjector gets campaign from Electron backend)

    ipcSend(
      generators.host.hostModeChanged(
        edition,
        matchmakeable,
        steamGuestLogin,
        guestAccount,
        credentials,
        hostAuthId,
      ),
    );
  }

  updateInvitation(invitationId: string, gameName: string, steamId: string): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Updating invitation via realtime');

    // TODO: Import and use realtime sendMessage
    // sendMessage(
    //   websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION, {
    //     invitationId,
    //     name: gameName,
    //     steamId,
    //   }),
    // );
  }

  killPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Killing Playjector');
    window.playcast.killPlayjector();
  }

  terminateGame(hostAuthId: string): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Terminating game process (pavilion)');
    ipcSend(generators.process.processTerminate(hostAuthId, 'pavilion'));
  }

  connectPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Connecting to Playjector');
    window.playcast.connectPlayjector();
  }

  onStateChange(callback: (state: PlayjectorState) => void): () => void {
    // Subscribe to the shared playjectorHostModeState store
    const listenerId = playjectorHostModeState.addListener((state) => {
      callback(state as PlayjectorState);
    });

    // Return unsubscribe function
    return () => {
      playjectorHostModeState.removeListener(listenerId);
    };
  }

  getCurrentState(): PlayjectorState {
    return playjectorHostModeState.currentState as PlayjectorState;
  }

  onCampaignLaunchResult(callback: (result: CampaignLaunchResult) => void): () => void {
    // Add listener
    this.campaignLaunchListeners.push(callback);

    // TODO: Hook into IPC pavilionLaunchReply messages
    // The IPC handler (apps/host/host-frontend/src/api/messaging/handlers/ipc.ts)
    // currently sends these directly to campaignSwitchTracker.
    // In the new architecture, we should route through this service.
    console.warn('[PlayjectorService] onCampaignLaunchResult: IPC integration not yet implemented');

    // Return unsubscribe function
    return () => {
      const index = this.campaignLaunchListeners.indexOf(callback);
      if (index > -1) {
        this.campaignLaunchListeners.splice(index, 1);
      }
    };
  }

  launchCampaign(hostAuthId: string, edition: PlaycastHostEdition, launchPackage: any): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] 🎮 Launching campaign via processLaunch IPC:', {
      edition,
      launcher: launchPackage.launcher,
      hostAuthId,
    });

    Sentry.logger.info(`[PlayjectorService] 🎮 Launching campaign via processLaunch IPC: ${edition}, ${launchPackage.launcher}`);

    ipcSend(
      generators.process.processLaunch(
        hostAuthId,
        edition,
        launchPackage,
      ),
    );
  }

  /**
   * Internal: Notify campaign launch listeners
   * This will be called by IPC handler when pavilionLaunchReply is received
   */
  public notifyCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[PlayjectorService] 📨 Notifying listeners of campaign launch result:', result);
    this.campaignLaunchListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (error) {
        console.error('[PlayjectorService] Campaign launch listener error:', error);
      }
    });
  }

  // ========================================
  // Stateful macro step IPC adapter
  // ========================================

  public async requestMacroStep(
    captures: Record<string, MacroStepCaptureSpec>,
    screenshotName?: string,
  ): Promise<Record<string, MacroStepCaptureResult>> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }

    if (this.pendingMacroStep) {
      // Playjector tracks one in-flight macro step at a time (see
      // ProcessDirectory::onMacroStep storing into m_ProcessInfo.macroVariants).
      // Two concurrent requests would have their replies aliased; reject early.
      throw new Error('A macro step request is already in flight');
    }

    if (Object.keys(captures).length === 0) {
      throw new Error('requestMacroStep called with empty captures map');
    }

    const variants: Record<string, MacroStepCaptureSpec> = {};
    for (const [captureId, spec] of Object.entries(captures)) {
      variants[captureId] = spec.region
        ? { width: spec.width, height: spec.height, region: spec.region }
        : { width: spec.width, height: spec.height };
    }

    const message = buildProcessMacroStepMessage(variants, screenshotName);

    const promise = new Promise<Record<string, MacroStepCaptureResult>>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.error(
          `[StatefulMacroSystemStep] ⏱️ processMacroStep reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms — Playjector did not reply`,
          {
            timeoutMs: MACRO_STEP_REPLY_TIMEOUT_MS,
            captureIds: Object.keys(variants),
            captureCount: Object.keys(variants).length,
          },
        );
        if (this.pendingMacroStep) {
          this.pendingMacroStep = null;
        }
        reject(new Error(`Macro step reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms`));
      }, MACRO_STEP_REPLY_TIMEOUT_MS);

      this.pendingMacroStep = { resolve, reject, timeoutId };
    });

    console.log(
      '[StatefulMacroSystemStep] 📤 Asking Playjector for screenshot(s) (processMacroStepMSG)',
      {
        variantCount: Object.keys(variants).length,
        captureIds: Object.keys(variants),
        variants: Object.fromEntries(
          Object.entries(variants).map(([id, spec]) => [
            id,
            {
              width: spec.width,
              height: spec.height,
              hasRegion: 'region' in spec && spec.region != null,
            },
          ]),
        ),
        timeoutMs: MACRO_STEP_REPLY_TIMEOUT_MS,
      },
    );
    ipcSend(message);

    return promise;
  }

  public notifyMacroStepResult(reply: MacroStepReply & { error?: string }): void {
    const waiter = this.pendingMacroStep;
    if (!waiter) {
      console.warn('[StatefulMacroSystemStep] ⚠️  Received Playjector macro step reply with no waiter — dropping');
      return;
    }
    this.pendingMacroStep = null;
    clearTimeout(waiter.timeoutId);

    if (reply.error) {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply error:', reply.error);
      waiter.reject(new Error(`Playjector macro step error: ${reply.error}`));
      return;
    }

    if (!reply.variantResults || typeof reply.variantResults !== 'object') {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply missing variantResults — rejecting', {
        replyShape: Object.keys(reply ?? {}),
      });
      waiter.reject(new Error('Macro step reply missing variantResults'));
      return;
    }

    const variantCount = Object.keys(reply.variantResults).length;
    console.log(
      '[StatefulMacroSystemStep] 📨 Received Playjector screenshot reply',
      {
        variantCount,
        captureIds: Object.keys(reply.variantResults),
        variantResults: Object.fromEntries(
          Object.entries(reply.variantResults).map(([id, r]) => [
            id,
            { width: r.width, height: r.height, hash: r.hash },
          ]),
        ),
      },
    );
    waiter.resolve(reply.variantResults);
  }

  // ========================================
  // Scheduled screenshot (delegates to macro step)
  // ========================================

  public async requestScheduledScreenshot(
    spec: ScheduledScreenshotSpec,
  ): Promise<ScheduledScreenshotResult> {
    const captures: Record<string, MacroStepCaptureSpec> = {
      [SCHEDULED_VARIANT_KEY]: { width: spec.width, height: spec.height },
    };

    const results = await this.requestMacroStep(captures, 'scheduled_latest');
    const result = results[SCHEDULED_VARIANT_KEY];

    if (!result) {
      throw new Error('Scheduled screenshot: no result returned for variant');
    }

    return { hash: result.hash, width: result.width, height: result.height };
  }

  public notifyScheduledScreenshotResult(
    _reply: ScheduledScreenshotResult & { error?: string },
  ): void {
    // No-op: scheduled screenshots now route through the macro step reply path.
    // Kept for interface compatibility with MockPlayjectorService.
    console.warn('[ScheduledScreenshot] notifyScheduledScreenshotResult is deprecated — replies route via macroStep');
  }
}

/**
 * Build a processMacroStepMSG IPC envelope.
 *
 * `processMacroStepMSG` does not yet have a Zod validator in `libs/Messaging`
 * (the wire schema authority is `apps/playjector/src/PlayJectorLib/source/
 * SchemaList.cpp::processMacroStepMSG`, consumed by
 * `ProcessDirectory::onMacroStep`), so we construct the envelope manually.
 * The TS shape, however, IS now in `libs/Messaging/src/types/messages/
 * process.ts` as `PlaycastMessageProcessMacroStep`, so the envelope is fully
 * typed end-to-end and host-electron's `messageHandler.ts` can narrow on
 * `header.action === 'macroStep'`.
 *
 * The optional `screenshotName` controls the filename Playjector uses when
 * saving the captured screenshot to disk (defaults to "macro_latest" in C++).
 */
function buildProcessMacroStepMessage(
  variants: PlaycastMessageProcessMacroStep['message']['variants'],
  screenshotName?: string,
): PlaycastMessage<PlaycastMessageProcessMacroStep> {
  return {
    signature: 'playcast',
    header: {
      tag: `${Date.now()}.macro-step`,
      source: 'host',
      schemaVersion: 5,
      timestamp: Date.now(),
      user: { id: 'host', auth: 'host' },
      target: 'process',
      action: 'macroStep',
      isReply: false,
    },
    body: {
      message: { variants, ...(screenshotName ? { screenshotName } : {}) },
      timings: [],
      stats: [],
      echo: false,
    },
  };
}
